import { AnalyticsAdapter, type AnalyticsConfig } from "./analytics";

// Singleton analytics instance
let analyticsInstance: AnalyticsAdapter | null = null;

/**
 * Creates and returns a singleton instance of the `AnalyticsAdapter` using the provided configuration.
 * If an instance already exists, it returns the existing instance.
 *
 * @param config - The configuration object for initializing the analytics adapter.
 * @returns The singleton instance of `AnalyticsAdapter`.
 */
export function createAnalytics(config: AnalyticsConfig): AnalyticsAdapter {
  if (!analyticsInstance) {
    analyticsInstance = new AnalyticsAdapter(config);
  }

  return analyticsInstance;
}

/**
 * Retrieves the current instance of the analytics adapter.
 *
 * @returns {AnalyticsAdapter | null} The analytics adapter instance if available, otherwise `null`.
 */
export function getAnalytics(): AnalyticsAdapter | null {
  return analyticsInstance;
}

/**
 * Retrieves the initialized analytics instance.
 *
 * @throws {Error} If the analytics instance has not been initialized.
 * @returns The analytics instance.
 * @remarks
 * Ensure that `createAnalytics()` has been called before using this hook.
 */
export function useAnalytics() {
  const analytics = getAnalytics();

  if (!analytics) {
    throw new Error("Analytics not initialized. Call createAnalytics() first.");
  }

  return analytics;
}

/**
 * @internal For test use only
 * DO NOT USE THIS IN APPLICATION CODE
 * */
export function __resetAnalyticsInstance() {
  analyticsInstance = null;
}
